/**
 * CreateBundleModal - Modal for creating an NFT bundle
 *
 * Flow:
 * 1. Select NFTs from user's collection
 * 2. Set bundle price
 * 3. Sign offers for each NFT (sequential signing)
 * 4. Bundle goes live after all signed
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Select } from '../../ui/components/Select';
import { FeatherX, FeatherCheck, FeatherLoader } from '@subframe/core';
import { NFTMultiSelect, SelectableNFT } from './NFTMultiSelect';
import {
  createBundle,
  confirmBundleOffer,
  getBundleExpirationOptions,
  BundleExpirationOption,
  XamanPayload,
  SigningIntentRef,
} from '../../api/bundles';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatPrice } from '../../utils/formatters';
import { toSigningPayload, getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import SigningStepMonitor from '../trades/SigningStepMonitor';

/** Backend payload entry (Xaman QR payload and/or client-signing intent). */
type BundlePayloadEntry = { nftTokenId: string; payload?: XamanPayload; signingIntent?: SigningIntentRef };

// Must match backend MIN_BUNDLE_PRICE_XRP in NFTBundleService.ts
const MIN_BUNDLE_PRICE_XRP = 0.01;

interface CreateBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName: string;
  userNfts: SelectableNFT[];
  onSuccess?: () => void;
}

type ModalStep = 'select' | 'price' | 'signing' | 'success' | 'error';

interface SigningProgress {
  currentIndex: number;
  totalCount: number;
  currentNftName: string;
  completedCount: number;
}

export const CreateBundleModal: React.FC<CreateBundleModalProps> = ({
  isOpen,
  onClose,
  collectionId,
  collectionName,
  userNfts,
  onSuccess,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  // State
  const [step, setStep] = useState<ModalStep>('select');
  const [selectedNftIds, setSelectedNftIds] = useState<Set<string>>(new Set());
  const [priceXrp, setPriceXrp] = useState<string>('');
  const [expiration, setExpiration] = useState<BundleExpirationOption>('7_days');
  const [error, setError] = useState<string>('');

  // Signing state
  const [bundleId, setBundleId] = useState<string>('');
  const [payloads, setPayloads] = useState<BundlePayloadEntry[]>([]);
  const [signingProgress, setSigningProgress] = useState<SigningProgress | null>(null);
  const [currentPayload, setCurrentPayload] = useState<BundlePayloadEntry | null>(null);
  const [websockets, setWebsockets] = useState<WebSocket[]>([]);
  // Track which NFTs have been signed (by index)
  const [signedIndices, setSignedIndices] = useState<Set<number>>(new Set());

  // Client-signing (joey-wc): no QR/WS — steps resolve SEQUENTIALLY via SigningStepMonitor.
  // Normalized payloads + the index of the step currently awaiting approval.
  const [isClientSigning, setIsClientSigning] = useState(false);
  const [normalizedPayloads, setNormalizedPayloads] = useState<Array<{ nftTokenId: string; payload: SigningPayload }>>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const bundleIdRef = useRef<string>('');
  const wcConfirmedRef = useRef<Set<number>>(new Set());

  // Polling fallback state (per-payload)
  const pollingIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const hasReachedFinalStateRef = useRef<boolean>(false);
  const MAX_POLLING_ATTEMPTS = 300;
  const POLLING_INTERVAL = 1000;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedNftIds(new Set());
      setPriceXrp('');
      setExpiration('7_days');
      setError('');
      setBundleId('');
      setPayloads([]);
      setSigningProgress(null);
      setCurrentPayload(null);
      setSignedIndices(new Set());
      hasReachedFinalStateRef.current = false;
      setIsClientSigning(false);
      setNormalizedPayloads([]);
      setCurrentStepIndex(0);
      bundleIdRef.current = '';
      wcConfirmedRef.current = new Set();
    }
  }, [isOpen]);

  // Cleanup websockets and polling on unmount
  useEffect(() => {
    return () => {
      websockets.forEach(ws => ws.close());
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };
  }, [websockets]);

  // Handle proceeding from selection to price
  const handleProceedToPrice = () => {
    if (selectedNftIds.size < 2) {
      setError('Please select at least 2 NFTs for a bundle');
      return;
    }
    setError('');
    setStep('price');
  };

  // Handle creating the bundle
  const handleCreateBundle = async () => {
    if (!user?.xrpl_address) {
      setError('Please sign in to create a bundle');
      return;
    }

    const price = parseFloat(priceXrp);
    if (isNaN(price) || price < MIN_BUNDLE_PRICE_XRP) {
      setError(`Minimum bundle price is ${MIN_BUNDLE_PRICE_XRP} XRP`);
      return;
    }

    setError('');

    try {
      console.log('[Bundle] Creating bundle with', selectedNftIds.size, 'NFTs, price:', price, 'XRP');
      const result = await createBundle({
        collectionId,
        nftTokenIds: Array.from(selectedNftIds),
        priceXrp: price,
        expiration,
        userAddress: user.xrpl_address,
      });

      console.log('[Bundle] Bundle created successfully:', result.bundleId);
      console.log('[Bundle] Received', result.payloads.length, 'payloads from backend');

      setBundleId(result.bundleId);
      bundleIdRef.current = result.bundleId;
      setPayloads(result.payloads);
      setStep('signing');

      // Detect provider from the first payload's signing intent. Client-signing
      // wallets (joey-wc) can't show N QRs or sign in parallel over one WC session.
      const provider = result.payloads[0]?.signingIntent?.provider ?? 'xaman';
      const clientSigning = getWalletProvider(provider as any).capabilities.clientSigning;
      setIsClientSigning(clientSigning);

      if (clientSigning) {
        // Sequential WC signing: normalize all, drive via SigningStepMonitor (no WS/QR).
        const normalized = result.payloads.map((p) => ({
          nftTokenId: p.nftTokenId,
          payload: toSigningPayload(p.signingIntent ?? p.payload),
        }));
        setNormalizedPayloads(normalized);
        wcConfirmedRef.current = new Set();
        setCurrentStepIndex(0);
        const firstNft = userNfts.find((n) => n.nftTokenId === result.payloads[0].nftTokenId);
        setSigningProgress({
          currentIndex: 0,
          totalCount: result.payloads.length,
          currentNftName: firstNft?.name || 'NFT 1',
          completedCount: 0,
        });
        return;
      }

      // Xaman path: set up ALL WebSocket listeners upfront to catch fast signing
      setupAllWebsockets(result.payloads, result.bundleId);

      // Show the first payload
      setCurrentPayload(result.payloads[0]);
      const firstNft = userNfts.find((n) => n.nftTokenId === result.payloads[0].nftTokenId);
      setSigningProgress({
        currentIndex: 0,
        totalCount: result.payloads.length,
        currentNftName: firstNft?.name || 'NFT 1',
        completedCount: 0,
      });
    } catch (err) {
      console.error('[Bundle] Error creating bundle:', err);
      setError(err instanceof Error ? err.message : 'Failed to create bundle');
    }
  };

  // Set up WebSocket listeners for ALL payloads upfront (with per-payload polling fallback)
  const setupAllWebsockets = useCallback((
    allPayloads: BundlePayloadEntry[],
    currentBundleId: string
  ) => {
    console.log('[Bundle] Setting up WebSockets for', allPayloads.length, 'payloads');
    console.log('[Bundle] Bundle ID:', currentBundleId);

    const confirmedSet = new Set<number>();
    const newWebsockets: WebSocket[] = [];
    const pollingAttemptsMap = new Map<number, number>();

    hasReachedFinalStateRef.current = false;

    const cleanupAll = () => {
      newWebsockets.forEach(ws => ws.close());
      pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };

    const checkAllComplete = () => {
      console.log('[Bundle] Checking completion:', confirmedSet.size, '/', allPayloads.length);
      if (confirmedSet.size === allPayloads.length) {
        // All signed and confirmed!
        console.log('[Bundle] All NFTs confirmed! Bundle complete.');
        hasReachedFinalStateRef.current = true;
        setStep('success');
        showSuccess(`Bundle with ${allPayloads.length} NFTs is now live!`);
        cleanupAll();
      }
    };

    const updateProgress = () => {
      // Find the first unconfirmed index to show
      for (let i = 0; i < allPayloads.length; i++) {
        if (!confirmedSet.has(i)) {
          const nft = userNfts.find((n) => n.nftTokenId === allPayloads[i].nftTokenId);
          console.log('[Bundle] Showing NFT', i + 1, 'for signing:', nft?.name || allPayloads[i].nftTokenId.slice(0, 12));
          setCurrentPayload(allPayloads[i]);
          setSigningProgress({
            currentIndex: i,
            totalCount: allPayloads.length,
            currentNftName: nft?.name || `NFT ${i + 1}`,
            completedCount: confirmedSet.size,
          });
          return;
        }
      }
    };

    // Handle a signed payload (shared between WS and polling)
    const handlePayloadSigned = async (index: number, txid: string) => {
      if (hasReachedFinalStateRef.current || confirmedSet.has(index)) return;

      const nft = userNfts.find((n) => n.nftTokenId === allPayloads[index].nftTokenId);
      const nftName = nft?.name || `NFT ${index + 1}`;

      console.log(`[Bundle] NFT ${index + 1} (${nftName}) SIGNED! txid:`, txid);

      try {
        console.log(`[Bundle] Confirming offer with backend for NFT ${index + 1}`);
        const confirmResult = await confirmBundleOffer({
          bundleId: currentBundleId,
          nftTokenId: allPayloads[index].nftTokenId,
          offerTxHash: txid,
        });
        console.log(`[Bundle] Backend confirmation result for NFT ${index + 1}:`, confirmResult);

        confirmedSet.add(index);
        setSignedIndices(new Set(confirmedSet));
        console.log(`[Bundle] Confirmed set now:`, Array.from(confirmedSet));

        // Clear polling for this payload if active
        const interval = pollingIntervalsRef.current.get(index);
        if (interval) {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(index);
        }

        if (confirmResult.bundleComplete) {
          console.log('[Bundle] Bundle marked complete by backend!');
          hasReachedFinalStateRef.current = true;
          setStep('success');
          showSuccess(`Bundle with ${allPayloads.length} NFTs is now live!`);
          cleanupAll();
        } else {
          updateProgress();
          checkAllComplete();
        }
      } catch (err) {
        console.error(`[Bundle] Error confirming offer for NFT ${index + 1}:`, err);
        if (!hasReachedFinalStateRef.current) {
          hasReachedFinalStateRef.current = true;
          setError(err instanceof Error ? err.message : 'Failed to confirm offer');
          setStep('error');
          cleanupAll();
        }
      }
    };

    // Poll for a specific payload by index
    const pollPayloadByIndex = async (index: number, uuid: string) => {
      if (hasReachedFinalStateRef.current || confirmedSet.has(index)) {
        const interval = pollingIntervalsRef.current.get(index);
        if (interval) {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(index);
        }
        return;
      }

      const nft = userNfts.find((n) => n.nftTokenId === allPayloads[index].nftTokenId);
      const nftName = nft?.name || `NFT ${index + 1}`;

      const attempts = (pollingAttemptsMap.get(index) || 0) + 1;
      pollingAttemptsMap.set(index, attempts);

      if (attempts > MAX_POLLING_ATTEMPTS) {
        if (!hasReachedFinalStateRef.current) {
          hasReachedFinalStateRef.current = true;
          setError(`Transaction request expired for ${nftName}. Please try again.`);
          setStep('error');
          cleanupAll();
        }
        return;
      }

      try {
        const response = await apiClient.get<any>(`/offers/payload/${uuid}`);
        if (!response.success || !response.data) return;

        const { signed, resolved, expired, dispatched, txid } = response.data;

        if (expired) {
          if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Transaction request expired for ${nftName}. Please try again.`);
            setStep('error');
            cleanupAll();
          }
          return;
        }

        if (signed === true && dispatched === 'tesSUCCESS') {
          await handlePayloadSigned(index, txid);
          return;
        }

        if (signed === true && dispatched && dispatched !== 'tesSUCCESS') {
          if (!hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Transaction failed for ${nftName}: ${dispatched}`);
            setStep('error');
            cleanupAll();
          }
          return;
        }

        if (signed === false && resolved === true) {
          if (!hasReachedFinalStateRef.current) {
            hasReachedFinalStateRef.current = true;
            setError(`Transaction was rejected for ${nftName}. Bundle creation cancelled.`);
            setStep('error');
            cleanupAll();
          }
          return;
        }
      } catch (err) {
        console.error(`[Bundle] Polling error for payload ${index}:`, err);
      }
    };

    // Start polling for a specific payload
    const startPollingForPayload = (index: number, uuid: string) => {
      if (pollingIntervalsRef.current.has(index)) return;

      console.log(`[Bundle] Starting polling fallback for payload ${index}`);
      pollingAttemptsMap.set(index, 0);

      pollPayloadByIndex(index, uuid);
      const interval = setInterval(() => pollPayloadByIndex(index, uuid), POLLING_INTERVAL);
      pollingIntervalsRef.current.set(index, interval);
    };

    allPayloads.forEach((item, index) => {
      const nft = userNfts.find((n) => n.nftTokenId === item.nftTokenId);
      const nftName = nft?.name || `NFT ${index + 1}`;

      // Xaman-only path: payload (with websocketUrl) is always present here.
      if (!item.payload) return;
      const xamanPayload = item.payload;

      console.log(`[Bundle] Creating WebSocket ${index + 1}/${allPayloads.length} for ${nftName}`);
      console.log(`[Bundle] WebSocket URL: ${xamanPayload.websocketUrl}`);

      const ws = new WebSocket(xamanPayload.websocketUrl);

      ws.onopen = () => {
        console.log(`[Bundle] WebSocket ${index + 1} OPENED for ${nftName}`);
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`[Bundle] WebSocket ${index + 1} (${nftName}) received:`, data);

          if (data.signed === true && data.txid) {
            await handlePayloadSigned(index, data.txid);
          } else if (data.signed === false) {
            console.log(`[Bundle] NFT ${index + 1} (${nftName}) was REJECTED`);
            if (!hasReachedFinalStateRef.current) {
              hasReachedFinalStateRef.current = true;
              setError(`Transaction was rejected for ${nftName}. Bundle creation cancelled.`);
              setStep('error');
              cleanupAll();
            }
          } else if (data.expired === true) {
            console.log(`[Bundle] NFT ${index + 1} (${nftName}) EXPIRED. Confirmed set:`, Array.from(confirmedSet));
            if (!confirmedSet.has(index)) {
              if (!hasReachedFinalStateRef.current) {
                console.log(`[Bundle] NFT ${index + 1} expired and was NOT confirmed - showing error`);
                hasReachedFinalStateRef.current = true;
                setError(`Transaction request expired for ${nftName}. Please try again.`);
                setStep('error');
                cleanupAll();
              }
            } else {
              console.log(`[Bundle] NFT ${index + 1} expired but was already confirmed - ignoring`);
            }
          }
        } catch (parseErr) {
          console.error(`[Bundle] Error parsing WebSocket message for NFT ${index + 1}:`, parseErr, event.data);
        }
      };

      ws.onerror = (err) => {
        console.error(`[Bundle] WebSocket ${index + 1} (${nftName}) ERROR:`, err);
        if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
          ws.close();
          startPollingForPayload(index, xamanPayload.uuid);
        }
      };

      ws.onclose = (event) => {
        console.log(`[Bundle] WebSocket ${index + 1} CLOSED for ${nftName}`, { code: event.code, reason: event.reason, wasClean: event.wasClean });
        if (!confirmedSet.has(index) && !hasReachedFinalStateRef.current) {
          startPollingForPayload(index, xamanPayload.uuid);
        }
      };

      newWebsockets.push(ws);
    });

    setWebsockets(newWebsockets);
    console.log('[Bundle] All WebSockets created');
  }, [userNfts, showSuccess]);

  // Client-signing (joey-wc) per-step resolution: confirm the offer with the backend,
  // then advance to the next NFT. Mirrors handlePayloadSigned but for sequential WC.
  const handleWcStepSigned = useCallback(async (index: number, txid: string) => {
    if (hasReachedFinalStateRef.current || wcConfirmedRef.current.has(index)) return;

    const allPayloads = normalizedPayloads;
    const nft = userNfts.find((n) => n.nftTokenId === allPayloads[index].nftTokenId);
    const nftName = nft?.name || `NFT ${index + 1}`;

    try {
      const confirmResult = await confirmBundleOffer({
        bundleId: bundleIdRef.current,
        nftTokenId: allPayloads[index].nftTokenId,
        offerTxHash: txid,
      });

      wcConfirmedRef.current.add(index);
      setSignedIndices(new Set(wcConfirmedRef.current));

      if (confirmResult.bundleComplete || wcConfirmedRef.current.size === allPayloads.length) {
        hasReachedFinalStateRef.current = true;
        setStep('success');
        showSuccess(`Bundle with ${allPayloads.length} NFTs is now live!`);
        return;
      }

      // Advance to the next NFT and update progress.
      const nextIndex = index + 1;
      setCurrentStepIndex(nextIndex);
      const nextNft = userNfts.find((n) => n.nftTokenId === allPayloads[nextIndex].nftTokenId);
      setSigningProgress({
        currentIndex: nextIndex,
        totalCount: allPayloads.length,
        currentNftName: nextNft?.name || `NFT ${nextIndex + 1}`,
        completedCount: wcConfirmedRef.current.size,
      });
    } catch (err) {
      console.error(`[Bundle] Error confirming WC offer for ${nftName}:`, err);
      if (!hasReachedFinalStateRef.current) {
        hasReachedFinalStateRef.current = true;
        setError(err instanceof Error ? err.message : 'Failed to confirm offer');
        setStep('error');
      }
    }
  }, [normalizedPayloads, userNfts, showSuccess]);

  const handleWcStepFailed = useCallback((message: string) => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;
    setError(message);
    setStep('error');
  }, []);

  // Continue signing from where we left off (if user closes and reopens)
  const handleContinueSigning = () => {
    if (payloads.length > 0 && bundleId) {
      setupAllWebsockets(payloads, bundleId);
      // Find first unsigned
      for (let i = 0; i < payloads.length; i++) {
        if (!signedIndices.has(i)) {
          const nft = userNfts.find((n) => n.nftTokenId === payloads[i].nftTokenId);
          setCurrentPayload(payloads[i]);
          setSigningProgress({
            currentIndex: i,
            totalCount: payloads.length,
            currentNftName: nft?.name || `NFT ${i + 1}`,
            completedCount: signedIndices.size,
          });
          break;
        }
      }
    }
  };

  const handleClose = () => {
    websockets.forEach(ws => ws.close());
    pollingIntervalsRef.current.forEach(interval => clearInterval(interval));
    pollingIntervalsRef.current.clear();
    if (step === 'success') {
      onSuccess?.();
    }
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'select':
        return 'Create Bundle';
      case 'price':
        return 'Set Bundle Price';
      case 'signing':
        return 'Sign NFT Offers';
      case 'success':
        return 'Bundle Created!';
      case 'error':
        return 'Error';
      default:
        return 'Create Bundle';
    }
  };

  const expirationOptions = getBundleExpirationOptions();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[560px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Step 1: Select NFTs */}
          {step === 'select' && (
            <div className="flex flex-col gap-4 w-full">
              <p className="text-body text-subtext-color">
                Select NFTs from {collectionName} to sell as a bundle
              </p>

              <NFTMultiSelect
                nfts={userNfts}
                selectedNftIds={selectedNftIds}
                onSelectionChange={setSelectedNftIds}
                maxSelection={50}
                collectionName={collectionName}
              />

              {error && (
                <p className="text-caption text-error-600">{error}</p>
              )}
            </div>
          )}

          {/* Step 2: Set Price */}
          {step === 'price' && (
            <div className="flex flex-col gap-4 w-full">
              <div className="bg-neutral-50 rounded-lg p-4">
                <p className="text-body-bold text-default-font">
                  {selectedNftIds.size} NFT{selectedNftIds.size > 1 ? 's' : ''} selected
                </p>
                <p className="text-caption text-subtext-color mt-1">
                  Buyers will purchase all NFTs together for a single price
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-body-bold text-default-font">
                  Bundle Price (XRP)
                </label>
                <input
                  type="number"
                  placeholder="Enter price"
                  value={priceXrp}
                  onChange={(e) => setPriceXrp(e.target.value)}
                  min="0.001"
                  step="0.001"
                  className="w-full rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                />
                <div className="flex flex-col gap-1 text-caption text-subtext-color">
                  <div className="flex justify-between">
                    <span>Price per NFT:</span>
                    <span>{priceXrp && selectedNftIds.size > 0
                      ? formatPrice(parseFloat(priceXrp) / selectedNftIds.size)
                      : '-'
                    }</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Marketplace fee (1%):</span>
                    <span>{priceXrp ? formatPrice(parseFloat(priceXrp) * 0.01) : '-'}</span>
                  </div>
                  <div className="flex justify-between text-default-font font-medium">
                    <span>You receive:</span>
                    <span>{priceXrp ? formatPrice(parseFloat(priceXrp) * 0.99) : '-'}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-body-bold text-default-font">
                  Expiration
                </label>
                <Select
                  value={expiration}
                  onValueChange={(value) => setExpiration(value as BundleExpirationOption)}
                >
                  {expirationOptions.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Item>
                  ))}
                </Select>
              </div>

              {/* How it works info */}
              <div className="bg-neutral-100 rounded-lg p-4 text-caption text-subtext-color">
                <p className="font-medium text-default-font mb-2">How bundle listing works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>You'll sign {selectedNftIds.size} transaction{selectedNftIds.size > 1 ? 's' : ''} in Xaman (one per NFT)</li>
                  <li>Each creates a sell offer to the xrp.deals marketplace</li>
                  <li>Offers cannot be accepted individually - it's all-or-nothing</li>
                  <li>When someone buys the bundle, all NFTs transfer at once</li>
                </ul>
              </div>

              {error && (
                <p className="text-caption text-error-600">{error}</p>
              )}
            </div>
          )}

          {/* Step 3: Signing — Xaman (QR + WS/polling, unchanged) */}
          {step === 'signing' && signingProgress && currentPayload && currentPayload.payload && !isClientSigning && (
            <div className="flex flex-col items-center gap-6 w-full">
              {/* Progress */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-subtext-color">
                    Signing NFT offers
                  </span>
                  <span className="text-body-bold text-default-font">
                    {signingProgress.completedCount + 1} of {signingProgress.totalCount}
                  </span>
                </div>
                <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-600 transition-all duration-300"
                    style={{
                      width: `${((signingProgress.completedCount + 1) / signingProgress.totalCount) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <p className="text-body text-subtext-color text-center">
                Sign the offer for: <span className="text-default-font font-medium">{signingProgress.currentNftName}</span>
              </p>

              {/* Price breakdown info box */}
              <div className="w-full bg-neutral-50 rounded-lg p-3">
                <div className="flex flex-col gap-1 text-caption">
                  <div className="flex justify-between">
                    <span className="text-subtext-color">Bundle price:</span>
                    <span className="text-default-font">{formatPrice(parseFloat(priceXrp))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-subtext-color">Marketplace fee (1%):</span>
                    <span className="text-default-font">-{formatPrice(parseFloat(priceXrp) * 0.01)}</span>
                  </div>
                  <div className="flex justify-between border-t border-neutral-200 pt-1 mt-1">
                    <span className="text-subtext-color">You receive (total):</span>
                    <span className="text-default-font font-medium">{formatPrice(parseFloat(priceXrp) * 0.99)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-subtext-color">Per NFT offer amount:</span>
                    <span className="text-default-font font-medium">
                      {formatPrice((parseFloat(priceXrp) * 0.99) / signingProgress.totalCount)}
                    </span>
                  </div>
                </div>
                <p className="text-caption text-subtext-color mt-2 italic">
                  Each offer is for your share after fees. When the bundle sells, you'll receive the total shown above.
                </p>
              </div>

              {/* QR Code */}
              <img
                src={currentPayload.payload.qrUrl}
                alt="Scan with Xaman"
                className="w-48 h-48 rounded-lg border border-neutral-border"
              />

              <a
                href={currentPayload.payload.deepLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button variant="brand-primary" className="w-full">
                  Open in Xaman
                </Button>
              </a>

              <p className="text-caption text-subtext-color flex items-center gap-2">
                <FeatherLoader className="w-4 h-4 animate-spin" />
                Waiting for signature...
              </p>
            </div>
          )}

          {/* Step 3: Signing — client-signing (joey-wc): sequential approve prompt, no QR */}
          {step === 'signing' && signingProgress && isClientSigning && normalizedPayloads[currentStepIndex] && (
            <div className="flex flex-col items-center gap-6 w-full">
              {/* Progress (shared shape with Xaman path) */}
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-subtext-color">
                    Signing NFT offers
                  </span>
                  <span className="text-body-bold text-default-font">
                    {signingProgress.completedCount + 1} of {signingProgress.totalCount}
                  </span>
                </div>
                <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-600 transition-all duration-300"
                    style={{
                      width: `${((signingProgress.completedCount + 1) / signingProgress.totalCount) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <FeatherLoader className="w-8 h-8 text-brand-600 animate-spin" />
              <p className="text-body text-default-font text-center">
                Approve "{signingProgress.currentNftName}" in your wallet ({signingProgress.completedCount + 1} of {signingProgress.totalCount})
              </p>

              {/* One monitor at a time (sequential). Keyed by payload id so a fresh
                  monitor mounts when we advance to the next NFT. */}
              <SigningStepMonitor
                key={normalizedPayloads[currentStepIndex].payload.id}
                payload={normalizedPayloads[currentStepIndex].payload}
                onSigned={(txHash) => handleWcStepSigned(currentStepIndex, txHash)}
                onRejected={() => handleWcStepFailed('Transaction was rejected. Bundle creation cancelled.')}
                onExpired={() => handleWcStepFailed('Signing request expired. Please try again.')}
                onError={(msg) => handleWcStepFailed(msg)}
              />
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <FeatherCheck className="w-8 h-8 text-success-600" />
              </div>
              <p className="text-body-bold text-default-font text-center">
                Bundle Created Successfully!
              </p>
              <p className="text-body text-subtext-color text-center">
                Your bundle with {selectedNftIds.size} NFT{selectedNftIds.size > 1 ? 's' : ''} for{' '}
                {formatPrice(parseFloat(priceXrp))} is now live.
              </p>
            </div>
          )}

          {/* Error */}
          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <FeatherX className="w-8 h-8 text-error-600" />
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'select' && (
              <>
                <Button variant="neutral-secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleProceedToPrice}
                  disabled={selectedNftIds.size < 2}
                >
                  Continue ({selectedNftIds.size} selected)
                </Button>
              </>
            )}

            {step === 'price' && (
              <>
                <Button variant="neutral-secondary" onClick={() => setStep('select')}>
                  Back
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleCreateBundle}
                  disabled={!priceXrp || parseFloat(priceXrp) <= 0}
                >
                  Create Bundle
                </Button>
              </>
            )}

            {step === 'signing' && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Cancel
              </Button>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default CreateBundleModal;
